import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, Stack, alpha, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FileTypePrompts from '../../fileTypePrompts';
import RadioGroup from '../../raidioGroup';

type Props = {
  questions: Record<string, any>;
  userResponses: Record<string, any>;
  maxHeight: number;
  onUserResponseChange: (questionId: string, response: any) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
};

const StepMultimodal = ({
  questions = {},
  userResponses = {},
  maxHeight,
  onUserResponseChange,
  onBack,
  onCancel,
  onNext,
}: Props) => {
  const getQuestionDef = (qId: string) => {
    if (!questions) return null;
    if (Array.isArray(questions)) {
      for (const item of questions) {
        if (item.id === qId) return item;
        if (item.questions && Array.isArray(item.questions)) {
          const found = item.questions.find((q: any) => q.id === qId);
          if (found) return found;
        }
      }
    } else if (typeof questions === 'object') {
      return questions[qId] || null;
    }
    return null;
  };

  const { t } = useTranslation();
  const theme = useTheme();

  const [containerRef, containerSize] = useElementSize();
  const [actionPanelRef, actionPanelSize] = useElementSize();

  const [errors, setErrors] = useState<any>({});

  const setMultiModalOption = (response: string) => {
    onUserResponseChange('multiModalOption', response);
  };

  const setFileTypePrompts = (response: string) => {
    onUserResponseChange('fileTypePrompts', response);
  };

  const handleNext = () => {
    const errors: any = {};
    let hasError = false;
    if (!userResponses.multiModalOption) {
      errors.multiModalOption = t('common.errors.requiredField');
      hasError = true;
    }
    if (userResponses.multiModalOption === 'customPrompts') {
    }

    setErrors(errors);
    if (!hasError) {
      onNext();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: `${maxHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack alignItems={'flex-start'} spacing={4}>
          <RadioGroup
            value={userResponses.multiModalOption || ''}
            options={(getQuestionDef('multiModalOption')?.options || [])}
            required
            error={errors.multiModalOption}
            fullWidth={false}
            onChange={setMultiModalOption}
          />
          {userResponses.multiModalOption === 'customPrompts' && (
            <Box alignSelf={'stretch'}>
              <FileTypePrompts
                label={(getQuestionDef('fileTypePrompts')?.questionText || getQuestionDef('fileTypePrompts')?.question || 'File Type Prompts')}
                ftPrompts={userResponses.fileTypePrompts || []}
                error={errors.fileTypePrompts}
                onChange={setFileTypePrompts}
                rows={3}
              />
            </Box>
          )}
        </Stack>
      </Box>
      <Box
        ref={actionPanelRef}
        sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            {t('common.buttons.back')}
          </Button>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="text"
              onClick={onCancel}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              disabled={!userResponses.multiModalOption}
              onClick={handleNext}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 2.5,
                color: 'text.primary',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {t('common.buttons.next')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default StepMultimodal;
